import React, { useState } from "react";

import CustomButton from "components/common/Button";
import { searchBillingCustomers } from "services/billing";

import { BsCustomer } from "./types";

/**
 * [PRD-2043] "Search by customer" filter shared by the account-wide
 * Subscriptions and Charges tabs.
 *
 * BS's subscription/charge list endpoints filter by a customer UUID, not a
 * name — so we resolve the typed name/email to a customer via the admin search
 * (first match) and hand its id up through `onChange`. Clearing resets to the
 * unfiltered list.
 */
export default function CustomerFilter({ onChange }: { onChange: (customerId?: string) => void }) {
  const [input, setInput] = useState("");
  const [resolved, setResolved] = useState<BsCustomer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const apply = async () => {
    const q = input.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    try {
      const res = await searchBillingCustomers({ q, limit: 1 });
      const customer = (res?.data?.[0] as BsCustomer | undefined) ?? null;
      if (customer) {
        setResolved(customer);
        onChange(customer.id);
      } else {
        setResolved(null);
        onChange(undefined);
        setError("No customer matched that name, email or monolith ID.");
      }
    } catch {
      setError("Customer search failed.");
    } finally {
      setSearching(false);
    }
  };

  const clear = () => {
    setInput("");
    setResolved(null);
    setError(null);
    onChange(undefined);
  };

  return (
    <div className="flex flex-col gap-025">
      <div className="flex gap-05 items-center">
        <input
          className="border rounded-lg px-075 py-05 para-body-m-regular"
          placeholder="Filter by customer name, email or monolith ID…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
          }}
          style={{ minWidth: 280 }}
        />
        <CustomButton label={searching ? "Searching…" : "Search"} onClick={apply} disabled={searching} />
        {resolved && <CustomButton label="Clear" onClick={clear} />}
      </div>
      {resolved && (
        <p className="para-body-xs-medium color-text-body-secondary">
          Showing results for {resolved.name} ({resolved.email})
        </p>
      )}
      {error && (
        <p className="para-body-xs-medium" style={{ color: "#d32f2f" }}>
          {error}
        </p>
      )}
    </div>
  );
}
